import * as React from "react"
import { Repository, SourceControlProvider } from "@modules/graphql/types"
import { useRepositoriesForInstallation } from "@modules/site/create/Import/hooks/useRepositoriesForInstallation"
import { Button, Text, ThemeCss } from "gatsby-interface"
import { navigate } from "gatsby"
import { getPathToImportSite } from "@modules/site/create/shared/utils"
import { MdArrowForward, MdLock } from "react-icons/md"
import Loading from "@modules/ui/components/Loading"
import { importSite as text } from "@modules/locales/default.js"
import { ErrorAlert } from "@modules/ui/components/ErrorAlert"
import { flexGrowCss } from "@modules/site/create/SiteCreationTypePicker"

const repoListCss: ThemeCss = theme => [
  flexGrowCss(theme),
  {
    margin: `${theme.space[5]} 0`,
    height: "240px",
    width: "100%",
    overflowY: "scroll",
  },
]

const repoItemCss: ThemeCss = theme => ({
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: `0 ${theme.space[4]}`,
})

const repoItemPrivateIconCss: ThemeCss = theme => ({
  marginLeft: theme.space[3],
  marginTop: theme.space[2],
})

const loadingCss: ThemeCss = theme => ({
  margin: `${theme.space[8]} 0`,
  display: `flex`,
  justifyContent: `center`,
  alignItems: `center`,
})

const emptyListCss: ThemeCss = theme => ({
  margin: `${theme.space[3]} ${theme.space[5]}`,
  fontSize: `inherit`,
  display: "flex",
  flexGrow: 1,
})

const handleRepoSelection = ({
  repo,
  organizationId,
  installationId,
  vcsConfigId,
  provider,
}: {
  repo: Pick<Repository, "nameWithOwner" | "id" | "name" | "isPrivate">
  organizationId: string
  installationId: string
  vcsConfigId?: string
  provider: SourceControlProvider
}) => () => {
  return navigate(
    `${getPathToImportSite(
      organizationId
    )}?provider=${provider}&installation=${installationId}&repository=${
      repo.name
    }${vcsConfigId ? `&configId=${vcsConfigId}` : ``}`
  )
}

const filterRepositories = (searchCriteria: string) => (
  item: Pick<Repository, "name">
) => {
  if (searchCriteria) {
    return item.name?.toLowerCase().includes(searchCriteria.toLowerCase())
  }

  return Boolean(item)
}

type SiteCreationRepositoryPicker = {
  installationId: string
  organizationId: string
  vcsConfigId?: string
  provider: SourceControlProvider
  isUserTeam: boolean
  searchCriteria: string
}

export function SiteCreationRepositoryPicker({
  installationId,
  organizationId,
  provider,
  isUserTeam,
  searchCriteria,
  vcsConfigId,
}: SiteCreationRepositoryPicker) {
  const [
    repositories,
    { loading, error, refetch },
  ] = useRepositoriesForInstallation(
    {
      installationId,
      provider,
      vcsConfigId,
      isUserTeam,
    },
    () => undefined
  )

  if (loading) {
    return (
      <Loading
        delay={1000}
        message={text.messages.loadingSourceProviderTeams}
        variant="baby"
        css={loadingCss}
      />
    )
  } else if (error) {
    return <ErrorAlert>{error.message}</ErrorAlert>
  }

  const repos =
    searchCriteria === ""
      ? repositories
      : repositories.filter(filterRepositories(searchCriteria))

  return (
    <div css={flexGrowCss}>
      {repos.length > 0 ? (
        <ul css={repoListCss}>
          {repos.map(repo => (
            <li key={repo.id} css={repoItemCss}>
              <span css={{ display: "flex", alignItems: "center" }}>
                <Text noMarginBottom>{repo.name}</Text>
                {repo.isPrivate && (
                  <span css={repoItemPrivateIconCss}>
                    <MdLock />
                  </span>
                )}
              </span>
              <Button
                variant="GHOST"
                onClick={handleRepoSelection({
                  repo,
                  organizationId,
                  installationId,
                  vcsConfigId,
                  provider,
                })}
                rightIcon={<MdArrowForward />}
              >
                {text.actions.import}
              </Button>
            </li>
          ))}
        </ul>
      ) : (
        <Text css={emptyListCss}>{text.messages.noRepos}</Text>
      )}

      <div
        css={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <span css={{ display: "flex" }}>
          <Text size="S" noMarginBottom>
            {text.messages.justAddedRepo}
          </Text>
          <Button variant="GHOST" size="S" onClick={() => refetch()}>
            {text.actions.refreshList}
          </Button>
        </span>
      </div>
    </div>
  )
}
